<template>
  <MainLayout>
    <!-- Loading -->
    <div v-if="loading" class="flex justify-center items-center py-40">
      <div class="flex flex-col items-center gap-y-4">
        <div
          class="w-10 h-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin"></div>
        <p class="text-lighter font-semibold">Artikel laden...</p>
      </div>
    </div>

    <template v-else-if="article">
      <!-- Parallax blobs -->
      <div class="parallax" data-parallax data-parallax-speed="0.2" data-parallax-direction="down">
        <div class="relative w-screen">
          <img
            class="absolute -left-[1vw] top-[-12vh] w-[20vw] opacity-60"
            src="/images/bg-visual-left-top.webp"
            alt="" />
          <img
            class="absolute -right-[6vw] top-[10vh] w-[28vw] opacity-40"
            src="/images/bg-visual-right-top.webp"
            alt="" />
        </div>
      </div>

      <!-- Breadcrumb + share nav -->
      <section id="blogpost" class="!pt-2 !pb-0">
        <div class="flex items-center justify-between gap-x-4 w-full -mb-8">
          <!-- Breadcrumb: standalone pill floating on background -->
          <nav
            class="inline-flex items-center gap-x-1.5 bg-white drop-shadow-sm rounded-full px-5 py-2.5 min-w-0 overflow-hidden"
            aria-label="Breadcrumb">
            <RouterLink
              :to="{ name: 'home.index' }"
              class="inline-flex items-center gap-x-1.5 text-lighter hover:text-primary transition-colors duration-200 text-sm font-medium flex-shrink-0">
              <i class="bi bi-house-fill text-[10px]"></i>
              <span class="hidden sm:inline">Home</span>
            </RouterLink>

            <i class="bi bi-chevron-right text-[9px] text-[#D0D1D8] flex-shrink-0"></i>

            <RouterLink
              :to="{ name: 'blogs.index' }"
              class="text-lighter hover:text-primary transition-colors duration-200 text-sm font-medium flex-shrink-0">
              Blog
            </RouterLink>

            <i class="bi bi-chevron-right text-[9px] text-[#D0D1D8] flex-shrink-0"></i>

            <span class="text-dark font-semibold text-sm truncate">{{ article.title }}</span>
          </nav>

          <!-- Share buttons: each is its own independent circle -->
          <div class="flex items-center gap-x-2 flex-shrink-0">
            <a
              :href="`https://wa.me/?text=${encodeURIComponent(article.title + ' ' + currentUrl)}`"
              target="_blank"
              rel="noopener noreferrer"
              title="Delen via WhatsApp"
              class="share-btn hover:bg-[#25D366] hover:text-white hover:border-[#25D366]">
              <i class="bi bi-whatsapp text-sm"></i>
            </a>

            <a
              :href="`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`"
              target="_blank"
              rel="noopener noreferrer"
              title="Delen via LinkedIn"
              class="share-btn hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2]">
              <i class="bi bi-linkedin text-sm"></i>
            </a>

            <a
              :href="`https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(article.title)}`"
              target="_blank"
              rel="noopener noreferrer"
              title="Delen via X"
              class="share-btn hover:bg-black hover:text-white hover:border-black">
              <i class="bi bi-twitter-x text-sm"></i>
            </a>

            <button
              :title="copied ? 'Gekopieerd!' : 'Kopieer link'"
              class="share-btn relative"
              :class="
                copied
                  ? 'bg-primary/10 border-primary/30 text-primary'
                  : 'hover:bg-primary/10 hover:text-primary hover:border-primary/30'
              "
              @click="copyLink">
              <Transition name="icon-swap">
                <i v-if="copied" key="check" class="bi bi-check-lg text-sm absolute"></i>
                <i v-else key="link" class="bi bi-link-45deg text-sm absolute"></i>
              </Transition>
            </button>

            <Transition name="fade">
              <span v-if="copied" class="text-xs font-semibold text-primary hidden sm:inline">
                Gekopieerd!
              </span>
            </Transition>
          </div>
        </div>

        <div
          class="relative w-full h-[380px] lg:h-[560px] rounded-2xl overflow-hidden drop-shadow-xl">
          <img
            v-if="article.image_url"
            :src="article.image_url"
            :alt="article.title"
            class="absolute inset-0 w-full h-full object-cover" />
          <div
            v-else
            class="absolute inset-0 bg-gradient-to-br from-[#FF3696]/20 to-[#FF7C53]/20" />

          <!-- Overlay gradient -->
          <div class="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />

          <!-- Title block at bottom -->
          <div class="absolute bottom-0 left-0 right-0 p-7 lg:p-12 z-10">
            <div class="flex items-center gap-x-3 mb-4">
              <span
                v-if="article.category"
                class="text-xs font-semibold text-white bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-3 py-1 rounded-full">
                {{ article.category.name }}
              </span>
              <span class="text-white/65 text-sm font-medium">{{
                formatDate(article.published_at)
              }}</span>
            </div>

            <h1
              class="text-white font-markPro text-[26px] leading-[32px] lg:text-[48px] lg:leading-[54px] font-bold tracking-[-0.06rem] max-w-[820px]">
              {{ article.title }}
            </h1>
          </div>
        </div>

        <div
          class="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-7 max-w-[1120px] lg:m-[auto] items-start">
          <!-- Left: article body -->
          <div class="flex flex-col gap-6">
            <!-- Lead / excerpt -->
            <div
              v-if="article.excerpt"
              class="bg-white rounded-2xl drop-shadow px-8 py-7 lg:px-12 lg:py-10">
              <p
                class="text-dark text-lg font-semibold leading-[1.75] border-l-[3px] border-gradient-primary pl-6 excerpt-border">
                {{ article.excerpt }}
              </p>
            </div>

            <!-- Body -->
            <div class="bg-white rounded-2xl drop-shadow px-8 py-8 lg:px-12 lg:py-12">
              <div class="prose" v-html="article.content" />
            </div>

            <!-- Footer: back link + share row -->
            <div
              class="flex items-center justify-between bg-white rounded-xl drop-shadow px-6 py-4">
              <RouterLink
                :to="{ name: 'blogs.index' }"
                class="inline-flex items-center gap-x-2 text-lighter font-semibold hover:text-primary transition-colors duration-200 text-sm">
                <i class="bi bi-arrow-left text-xs"></i>
                Alle artikelen
              </RouterLink>

              <div class="flex items-center gap-x-2">
                <span class="text-sm text-lighter font-medium mr-1">Delen:</span>
                <a
                  :href="`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="w-8 h-8 rounded-full bg-[#F4F6FB] hover:bg-[#0A66C2] hover:text-white text-lighter flex items-center justify-center transition-all duration-200">
                  <i class="bi bi-linkedin text-sm"></i>
                </a>
                <a
                  :href="`https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(article.title)}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="w-8 h-8 rounded-full bg-[#F4F6FB] hover:bg-black hover:text-white text-lighter flex items-center justify-center transition-all duration-200">
                  <i class="bi bi-twitter-x text-sm"></i>
                </a>
                <button
                  class="w-8 h-8 rounded-full bg-[#F4F6FB] hover:bg-primary hover:text-white text-lighter flex items-center justify-center transition-all duration-200"
                  @click="copyLink">
                  <i class="bi bi-link-45deg text-sm"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Right: sticky sidebar -->
          <div class="flex flex-col gap-5 lg:sticky lg:top-8">
            <!-- Article info card -->
            <div class="bg-white rounded-xl drop-shadow p-6 flex flex-col gap-y-4">
              <span
                v-if="article.category"
                class="inline-flex self-start text-xs font-semibold text-white bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-3 py-1 rounded-full">
                {{ article.category.name }}
              </span>

              <div class="flex flex-col gap-y-2 text-sm text-lighter">
                <div class="flex items-center gap-x-2">
                  <i class="bi bi-calendar3 text-xs"></i>
                  <span>{{ formatDate(article.published_at) }}</span>
                </div>
              </div>

              <div class="pt-3 border-t border-[#ECEDF0]">
                <div class="flex items-center gap-x-3">
                  <div
                    class="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF3696] to-[#FF7C53] flex items-center justify-center flex-shrink-0">
                    <span class="text-white text-xs font-bold">B</span>
                  </div>
                  <div>
                    <p class="text-dark font-semibold text-sm leading-none">Bistroo Redactie</p>
                    <p class="text-lighter text-xs mt-0.5">bistroo.nl</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Mini CTA card -->
            <div class="relative rounded-xl overflow-hidden p-6 flex flex-col gap-y-4">
              <div class="absolute inset-0 bg-[#E3EEF7]"></div>
              <img
                class="absolute -right-6 -bottom-4 w-32 opacity-30 rotate-[-15deg]"
                src="/images/bg-visual-right-top.webp"
                alt="" />
              <div class="relative z-10 flex flex-col gap-y-3">
                <p
                  class="font-markPro font-bold text-dark text-lg leading-snug tracking-[-0.03rem]">
                  Serveer
                  <span class="text-gradient-primary">meer orders</span> in <br />
                  jouw restaurant met Bistroo
                </p>
                <p class="text-lighter text-sm font-medium leading-relaxed">
                  Open vandaag je shop. Het is gratis! Genereer binnen enkele minuten je eerste
                  bestelling
                </p>
                <a
                  v-track-event="'onboarding_start'"
                  href="https://merchant.bistroo.nl/register"
                  class="btn btn-primary btn-sm inline-flex items-center gap-x-2 self-start mt-1">
                  Gratis starten
                  <i class="bi bi-arrow-right text-xs"></i>
                </a>
              </div>
            </div>

            <!-- Reading progress card -->
            <div class="bg-white rounded-xl drop-shadow p-6">
              <div class="flex items-center justify-between mb-3">
                <p class="text-sm font-semibold text-dark">Voortgang</p>
                <p class="text-sm font-semibold text-gradient-primary">
                  {{ Math.round(scrollProgress) }}%
                </p>
              </div>
              <div class="w-full h-1.5 bg-[#F4F6FB] rounded-full overflow-hidden">
                <div
                  class="h-full bg-gradient-to-r from-[#FF3696] to-[#FF7C53] rounded-full transition-all duration-200"
                  :style="{ width: `${scrollProgress}%` }" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>

    <!-- Not found -->
    <div v-else-if="!loading" class="flex justify-center items-center py-40">
      <div class="bg-white rounded-xl drop-shadow p-10 text-center max-w-[400px]">
        <i class="bi bi-journal-x text-5xl text-lighter mb-4 block"></i>
        <p class="text-dark font-bold text-lg">Artikel niet gevonden</p>
        <p class="text-lighter mt-2 mb-6">Dit artikel bestaat niet of is verwijderd.</p>
        <RouterLink :to="{ name: 'blogs.index' }" class="btn btn-primary">
          Terug naar blog
        </RouterLink>
      </div>
    </div>

    <!-- CTA -->
    <section id="cta" class="backgroundgradient backgroundgradient-bottom">
      <div class="w-full rounded-2xl py-16 max-md:px-14 lg:pt-26 lg:pb-20 relative overflow-hidden">
        <div class="absolute inset-0 z-10 bg-[#E3EEF7]"></div>
        <div
          class="parallax"
          data-parallax
          data-parallax-speed="0.05"
          data-parallax-direction="down">
          <div class="relative w-full h-full">
            <img
              class="absolute top-0 left-0 -translate-x-1/3 h-[150%] rotate-[45%]"
              src="/images/bg-visual-left-top.webp"
              alt="" />
            <img
              class="absolute right-0 translate-x-1/3 -top-1/4 lg:-top-1/2 h-1/2 lg:h-full rotate-[-20deg]"
              src="/images/bg-visual-right-top.webp"
              alt="" />
          </div>
        </div>

        <div
          class="flex flex-col gap-y-5.5 justify-center items-center text-center relative z-30 lg:min-w-[1136px]">
          <h4
            class="text-[32px] leading-[42px] lg:text-5xl font-bold text-dark text-center font-markPro block tracking-[-0.08rem]">
            {{ t('main.cta_block.open_your_webshop_today') }}
            <span class="text-gradient-primary whitespace-nowrap">{{
              t('main.cta_block.its_free')
            }}</span>
          </h4>
          <p class="text-lg lg:text-[24px] text-lighter font-semibold">
            {{ t('main.cta_block.start_generating_more_orders') }}
          </p>
          <a
            v-track-event="'onboarding_start'"
            href="https://merchant.bistroo.nl/register"
            class="btn btn-lg btn-primary flex items-center gap-x-3 mt-4">
            <span>{{ t('main.cta_block.get_started_for_free') }}</span>
            <i class="bi bi-arrow-right text-lg mt-[3px]"></i>
          </a>
        </div>
      </div>
    </section>
  </MainLayout>
</template>

<script setup lang="ts">
import { useBlogHttp, type ShowBlogPost } from '@/http/useBlogHttp'
import MainLayout from '@/layouts/MainLayout.vue'
import { useHead } from '@unhead/vue'
import { computed, onMounted, onServerPrefetch, onUnmounted, ref, useSSRContext, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const { t } = useI18n()
const route = useRoute()

const { show } = useBlogHttp()

const article = ref<ShowBlogPost | undefined>(undefined)
const loading = ref(true)
const scrollProgress = ref(0)

// TODO: Why..?
const currentUrl = ref('')

const copied = ref(false)

const ssrContext = useSSRContext()
const CANONICAL_HOSTS = new Set(['www.bistroo.nl', 'www.bistroo.com', 'staging.bistroo.com'])
const DEFAULT_CANONICAL_HOST = 'www.bistroo.nl'

const resolveCanonicalOrigin = (host: string): string => {
  const hostname = host.split(':')[0].toLowerCase()

  if (CANONICAL_HOSTS.has(hostname)) {
    return `https://${hostname}`
  }

  return `https://${DEFAULT_CANONICAL_HOST}`
}

useHead(
  computed(() => {
    const hostname = ssrContext ? ssrContext.hostname : new URL(window.location.href).hostname
    const origin = resolveCanonicalOrigin(hostname)

    return {
      title: article.value ? `${article.value.title} | Bistroo Blog` : 'Blog | Bistroo',
      meta: [{ name: 'description', content: article.value?.excerpt ?? '' }],
      link: [{ rel: 'canonical', href: `${origin}${route.path}` }],
    }
  })
)

const onScroll = () => {
  const section = document.getElementById('blogpost')
  if (!section) {
    return
  }
  const sectionTop = section.offsetTop
  const sectionHeight = section.offsetHeight
  const midViewport = window.scrollY + window.innerHeight / 2
  const progress = (midViewport - sectionTop) / sectionHeight
  scrollProgress.value = Math.min(100, Math.max(0, progress * 100))
}

onMounted(() => {
  currentUrl.value = window.location.href
  window.addEventListener('scroll', onScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})

const copyLink = () => {
  navigator.clipboard.writeText(currentUrl.value)
  copied.value = true
  setTimeout(() => {
    copied.value = false
  }, 2500)
}

const loadPost = async (slug: string) => {
  loading.value = true
  scrollProgress.value = 0

  try {
    const res = await show(slug)

    article.value = res.data
  } catch (err) {
    console.error(err)
  } finally {
    loading.value = false
  }
}

onServerPrefetch(async () => {
  await loadPost(String(route.params.slug))
})

watch(
  () => route.params.slug,
  (slug) => {
    if (slug) loadPost(String(slug))
  },
  { immediate: true }
)

// TODO: Duplicate
const formatDate = (dateString: string | null): string => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
}
</script>

<style scoped>
@reference "@/assets/css/main.css";

.excerpt-border {
  border-left-color: #ff3696;
}

.share-btn {
  @apply w-9 h-9 rounded-full border border-[#ECEDF0] bg-white text-lighter flex items-center justify-center transition-all duration-200 relative overflow-hidden flex-shrink-0;
}

.icon-swap-enter-active,
.icon-swap-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
  position: absolute;
}

.icon-swap-enter-from {
  opacity: 0;
  transform: scale(0.5) rotate(-20deg);
}

.icon-swap-leave-to {
  opacity: 0;
  transform: scale(0.5) rotate(20deg);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* TODO: Tom's hacky fix */
:deep(.prose) {
  @apply text-dark leading-relaxed;

  h2 {
    @apply text-[22px] font-bold font-markPro tracking-[-0.04rem] mt-10 mb-4 text-dark;
  }

  h2:first-child {
    @apply mt-0;
  }

  h3 {
    @apply text-lg font-bold font-markPro mt-8 mb-3 text-dark;
  }

  p {
    @apply text-lighter mb-5 leading-[1.85] text-[15px];
  }

  ul {
    @apply list-disc pl-6 mb-5 text-lighter;
    li {
      @apply mb-2 text-[15px] leading-[1.75];
    }
  }

  ol {
    @apply list-decimal pl-6 mb-5 text-lighter;
    li {
      @apply mb-2 text-[15px] leading-[1.75];
    }
  }

  strong,
  b {
    @apply font-semibold text-dark;
  }

  em {
    @apply italic;
  }

  a {
    @apply text-primary underline hover:no-underline;
  }

  blockquote {
    @apply rounded-xl bg-[#F4F6FB] border-l-4 px-6 py-5 my-8 text-dark font-semibold text-[15px] leading-[1.75] italic;
    border-left-color: #ff3696;
  }
}
</style>
